import { Dayjs } from 'dayjs';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SerialNumberScope } from './serial-number-scope.js';
import { SerialNumberStrategy } from './serial-number-strategy.js';
import { StandardSerialNumberPool } from './standard-serial-number-pool.js';
import { EventTicket } from '../event-ticket.js';
import { OpenAccessPass } from '../open-access-pass/open-access-pass.js';
import { UnsupportedProductType } from './unsupported-product-type.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { cloneMap, isSameData, isSameMap, mapToData } from '../../dto/data-model-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { getTicketTypeShortCode, TicketType } from '../ticket-type.js';
import { Price } from '../price.js';
import { LegacyPriceBasedSerialNumberPool } from './legacy-price-based-serial-number-pool.js';
import { EventProfile } from '../event-profile.js';
import { TrueTime } from '../../../time/true-time.js';
import { VersionRange } from '../../core/version-range.js';
import { TicketGiftCard } from '../discounts/ticket-gift-card.js';
import { OpenTicketPass } from '../discounts/open-ticket-pass.js';
import { GiftCard } from '../gift-card/gift-card.js';
import { TicketPricing } from '../../../payment/ticket-pricing.js';
import { SerialNumberPricingContext } from './serial-number-pool.js';

export class PriceBasedSerialNumberPool extends StandardSerialNumberPool {
  static readonly TYPE_ID: string = '62fd2f7dd59c4c6da8953b603b8a0083';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string | null,
    public readonly seasonId: string | null,
    public readonly creationDate: Dayjs,
    public readonly shortCode: string,
    public readonly scope: SerialNumberScope,
    public readonly serialNumberBuckets: Map<string, number>,
  ) {
    super();
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    eventId: string | null,
    seasonId: string | null,
    creationDate: Dayjs,
    shortCode: string,
    scope: SerialNumberScope,
    serialNumberBuckets: Map<string, number>,
  }): PriceBasedSerialNumberPool {
    return new PriceBasedSerialNumberPool(
        args.businessRef,
        args.eventId,
        args.seasonId,
        args.creationDate,
        args.shortCode,
        args.scope,
        args.serialNumberBuckets,
    );
  }

  get $typeId(): string {
    return PriceBasedSerialNumberPool.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PriceBasedSerialNumberPool.SCHEMA_VERSION);
  }

  get strategy(): SerialNumberStrategy {
    return SerialNumberStrategy.AssignByPrice;
  }

  static fromLegacy(legacyPool: LegacyPriceBasedSerialNumberPool): PriceBasedSerialNumberPool {
    return PriceBasedSerialNumberPool.create({
      businessRef: legacyPool.businessRef,
      eventId: legacyPool.eventId,
      seasonId: null,
      creationDate: TimeZone.cloneDate(legacyPool.creationDate),
      scope: legacyPool.scope,
      shortCode: legacyPool.shortCode,
      serialNumberBuckets: cloneMap(legacyPool.getSerialNumbers(), (v) => v),
    });
  }

  static forBusiness(businessRef: BusinessModuleRef, shortCode: string): PriceBasedSerialNumberPool {
    return PriceBasedSerialNumberPool.create({
      businessRef: businessRef,
      eventId: null,
      seasonId: null,
      creationDate: TrueTime.now(),
      scope: SerialNumberScope.Business,
      shortCode: shortCode,
      serialNumberBuckets: new Map<string, number>(),
    });
  }

  static forEvent(event: EventProfile): PriceBasedSerialNumberPool | null {
    if(event.shortCode == null) {
      return null;
    }

    return PriceBasedSerialNumberPool.create({
      businessRef: event.businessRef,
      eventId: event.uniqueId,
      seasonId: null,
      creationDate: TrueTime.now(),
      scope: SerialNumberScope.Event,
      shortCode: event.shortCode,
      serialNumberBuckets: new Map<string, number>(),
    });
  }

  protected getFormattedSeasonId(product: IProductWithSerialNumber): Result<string | null, ErrorLike> {
    return Result.ok(this.seasonId);
  }

  /**
   * Supply the ticket's shared pricing breakdown to include order/entry discounts.
   * Without context only the ticket's own discount can be applied.
   * @param {IProductWithSerialNumber} product Product receiving a serial number.
   * @param {SerialNumberPricingContext} pricingContext Shared pricing for this ticket.
   * @return {Result<string, ErrorLike>} Assigned serial number, or an error without consuming a counter.
   */
  override getSerialNumber(product: IProductWithSerialNumber, pricingContext?: SerialNumberPricingContext): Result<string, ErrorLike> {
    if(pricingContext != null) {
      const pricing = pricingContext.ticketPricing;
      if(!(product instanceof EventTicket) || pricing.ticket !== product) {
        return Result.error('Serial number pricing must belong to this ticket.');
      }
      if(!TicketPricing.isValidTicketBreakdown(pricing)) {
        return Result.error('Serial number pricing has invalid prices or inconsistent discounts.');
      }
    }
    return super.getSerialNumber(product, pricingContext);
  }

  protected getFormattedProductGroupId(product: IProductWithSerialNumber, pricingContext?: SerialNumberPricingContext): Result<string, ErrorLike> {
    switch(true) {
      case product instanceof EventTicket: {
        return this.findDiscountedPriceOfEventTicket(product, pricingContext)
            .map((price) => price.convertToCents.toFixed().padStart(3, '0'));
      } break;
      case product instanceof OpenAccessPass: {
        const price = product.price;

        return Result.ok(price.convertToCents.toFixed().padStart(3, '0'));
      } break;
      case product instanceof TicketGiftCard: {
        const price = product.value;

        return Result.ok(price.convertToCents.toFixed().padStart(3, '0'));
      } break;
      case product instanceof GiftCard: {
        const price = product.price;

        return Result.ok(price.convertToCents.toFixed().padStart(3, '0'));
      } break;
      case product instanceof OpenTicketPass: {
        const price = product.productPrice;

        return Result.ok(price.convertToCents.toFixed().padStart(3, '0'));
      } break;
      default: {
        return Result.error(new UnsupportedProductType(product));
      } break;
    }
  }

  private findDiscountedPriceOfEventTicket(product: EventTicket, pricingContext?: SerialNumberPricingContext): Result<Price, ErrorLike> {
    if(product.ticketType === TicketType.Protocol) {
      return Result.ok(Price.zero(product.template.price.currency));
    }
    if(pricingContext != null) {
      return Result.ok(pricingContext.ticketPricing.finalPrice);
    }
    const pricing = TicketPricing.calculate({ tickets: [product], discount: null });
    return pricing != null ? Result.ok(pricing.tickets[0].finalPrice) : Result.error('Cannot calculate ticket pricing.');
  }

  protected getProductSerialNumberBucketId(
      product: IProductWithSerialNumber,
      pricingContext?: SerialNumberPricingContext,
  ): Result<string, ErrorLike> {
    switch(true) {
      case product instanceof EventTicket: {
        const shortCode = getTicketTypeShortCode(product.ticketType);
        if(product.ticketType === TicketType.Protocol) {
          return Result.ok(`${shortCode}_${product.template.price.encode()}`);
        }

        return this.findDiscountedPriceOfEventTicket(product, pricingContext)
            .map((price) => `${shortCode}_${price.encode()}`);
      } break;
      case product instanceof OpenAccessPass: {
        return Result.ok(`p_${product.price.encode()}`);
      } break;
      case product instanceof GiftCard: {
        return Result.ok(`g_${product.price.encode()}`);
      } break;
      case product instanceof OpenTicketPass: {
        return Result.ok(`v_${product.productPrice.encode()}`);
      } break;
      case product instanceof TicketGiftCard: {
        return Result.ok(`v_${product.value.encode()}`);
      } break;
      default: {
        return Result.error(new UnsupportedProductType(product));
      } break;
    }
  }

  static fromMap(context: SerializationContext, map: SerializedData): PriceBasedSerialNumberPool {
    return PriceBasedSerialNumberPool.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getStringOrNull(map, 'eventId'),
      seasonId: MapUtils.getStringOrNull(map, 'seasonId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      scope: toEnumFromString(MapUtils.getString(map, 'scope'), SerialNumberScope),
      shortCode: MapUtils.getString(map, 'shortCode'),
      serialNumberBuckets: MapUtils.getMap(map, 'serialNumberBuckets', (k, v) => ({
        key: JSONUtils.decodeString(k),
        value: JSONUtils.decodeNumber(v),
      })),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      businessRef: this.businessRef.encode(),
      eventId: this.eventId,
      seasonId: this.seasonId,
      creationDate: context.encodeDate(this.creationDate),
      scope: fromEnumAsString(this.scope),
      shortCode: this.shortCode,
      serialNumberBuckets: mapToData(this.serialNumberBuckets, (k, v) => ({
        key: k,
        value: v,
      })),
    };
  }

  clone(): this {
    return PriceBasedSerialNumberPool.create({
      businessRef: this.businessRef.clone(),
      eventId: this.eventId,
      seasonId: this.seasonId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      scope: this.scope,
      shortCode: this.shortCode,
      serialNumberBuckets: cloneMap(this.serialNumberBuckets, (v) => v),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      other.seasonId === this.seasonId &&
      other.creationDate === this.creationDate &&
      other.shortCode === this.shortCode &&
      other.scope === this.scope &&
      isSameMap(other.serialNumberBuckets, this.serialNumberBuckets, (lh, rh) => lh === rh);
  }

  resetForNewSeason(seasonId: string): this {
    return PriceBasedSerialNumberPool.create({
      businessRef: this.businessRef.clone(),
      eventId: this.eventId,
      seasonId: seasonId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      scope: this.scope,
      shortCode: this.shortCode,
      serialNumberBuckets: new Map<string, number>(),
    }) as this;
  }
}

export class PriceBasedSerialNumberPoolBuilder extends DataModelBuilder<PriceBasedSerialNumberPool> {
  build(context: SerializationContext, map: SerializedData): PriceBasedSerialNumberPool {
    return PriceBasedSerialNumberPool.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(PriceBasedSerialNumberPool.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PriceBasedSerialNumberPool.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // in this version we added `seasonId` of type `number | null`
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
